import type { DateTime } from "luxon";
import { usePickOpenShift } from "../components/OpenShiftCard/hooks";
import { useSnackbar } from "../contexts/SnackbarContext";
import type { OpenShift } from "../utils/models";
import { usePickMergedShifts } from "./useOpenShiftActions";

interface PickShiftParams {
	id: string;
	departmentId: string;
	startTime: DateTime;
	endTime: DateTime;
	isMerged?: boolean;
	originalShifts?: OpenShift[];
	partial?: boolean;
	bonusAtPickup?: number;
	hoursLeftAtPickup?: number | null;
}

type PickOpenShiftOptions = Parameters<typeof usePickOpenShift>[0];
type PickMergedShiftOptions = Parameters<typeof usePickMergedShifts>[0];

export const usePickShift = (options?: PickOpenShiftOptions | PickMergedShiftOptions) => {
	const { mutateAsync: pickSingle, isPending: isPickingSingle } = usePickOpenShift(options);
	const { mutateAsync: pickMerged, isPending: isPickingMerged } = usePickMergedShifts(options);
	const { showSnackbar } = useSnackbar();

	const pickShift = async (params: PickShiftParams) => {
		if (params.isMerged) {
			if (!params.originalShifts) {
				// TODO - I took this logic from existing code we had in OpenShifts page - I am not sure what should happen in this case but we should fail and alert for sure.
				showSnackbar("Original shifts are required to pick a merged shift, unable to pick the shift.", "error");
				return;
			}
			return pickMerged({
				departmentId: params.departmentId,
				shiftIds: params.originalShifts.map((s) => s.id),
				acceptedStartTime: params.startTime,
				acceptedEndTime: params.endTime,
				partial: params.partial ?? false,
			});
		}
		return pickSingle({
			openShiftId: params.id,
			departmentId: params.departmentId,
			selectedStart: params.startTime,
			selectedEnd: params.endTime,
			partial: params.partial,
			bonusAtPickup: params.bonusAtPickup,
			hoursLeftAtPickup: params.hoursLeftAtPickup,
		});
	};

	return {
		pickShift,
		isPicking: isPickingSingle || isPickingMerged,
	};
};
